import React, { useEffect, useMemo, useRef, useState } from "react";
import { getDisplayPlatforms } from "../utils/networkNames";
import PropTypes from "prop-types";
import { CalendarClock, CheckCircle2, Download, Heart, MoreHorizontal, Trash2, Undo2, Upload } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import SubpageHeader from "../components/SubpageHeader";
import SiteFooter from "../components/SiteFooter";
import UtilityPageIntro from "../components/UtilityPageIntro";
import ContentSection from "../components/ContentSection";
import Button from "../components/ui/Button";
import Chip from "../components/ui/Chip";
import DropdownMenu from "../components/ui/DropdownMenu";
import Typography from "../components/ui/Typography";
import PosterPlaceholder from "../components/ui/PosterPlaceholder";
import SeasonBadge from "../components/ui/SeasonBadge";
import ShowRecommendationCard from "../components/ShowRecommendationCard";
import BridgeText from "../components/BridgeText";
import usePageMeta from "../hooks/usePageMeta";
import useWatchlist from "../hooks/useWatchlist";
import { announce } from "../utils/announce";
import { cn } from "../utils/classNames";
import { handleInternalLinkClick } from "../utils/navigation";
import { exportWatchlist, parseBackupImport } from "../utils/exportWatchlist";
import { trackEvent } from "../utils/analytics";
import {
  formatSavedReleaseDate,
  getReleaseRadarLabel,
  getRemovalAnnouncement,
  getRestoreAnnouncement,
  planRemoval,
  splitReleaseRadarEntries,
} from "../utils/watchlist";
import { checkShowSlug, fetchReleases } from "../services/backendApi";

const MotionSection = motion.section;

const BACKUP_REMINDER_COUNT = 3;

const EXPORT_OPTIONS = [
  { value: "csv", label: "Spreadsheet CSV" },
  { value: "imdb", label: "IMDb ID list" },
  { value: "tvdb", label: "TVDB ID list" },
  { value: "trakt", label: "Trakt sync JSON" },
  { value: "backup", label: "Backup file" },
];

const getPrimaryNetwork = (show) => getDisplayPlatforms(show) || null;

const getShowHref = (show) => (show.slug ? `/show/${show.slug}` : null);

const getRowRecommendation = (show) => {
  const nextWatch = show?.nextWatch;
  if (!nextWatch?.slug || !nextWatch?.title) {
    return null;
  }
  if (show?.slug && nextWatch.slug === show.slug) {
    return null;
  }
  return nextWatch;
};

const getRecommendationBridge = (show) => {
  const recommendation = getRowRecommendation(show);
  const bridge = recommendation?.bridge;
  if (typeof bridge !== "string" || !bridge.trim()) {
    return "";
  }
  return bridge.trim();
};

const normalizeRecommendationTitle = (value = "") => String(value || "").trim().toLowerCase();

const getRecommendationKey = (show = {}) => {
  if (!show || typeof show !== "object") {
    return null;
  }
  if (show.slug) return `slug:${show.slug}`;
  if (show.id) return `id:${show.id}`;
  if (show.title) return `title:${normalizeRecommendationTitle(show.title)}`;
  return null;
};

const getRecommendationKeys = (show = {}) => {
  if (!show || typeof show !== "object") {
    return [];
  }
  return [
    show.slug ? `slug:${show.slug}` : null,
    show.id ? `id:${show.id}` : null,
    show.title ? `title:${normalizeRecommendationTitle(show.title)}` : null,
  ].filter(Boolean);
};

const buildWatchlistRecommendations = (shows = [], limit = 3) => {
  const watchlistKeys = new Set(
    shows
      .flatMap((show) => getRecommendationKeys(show))
  );
  const byRecommendation = new Map();

  for (const sourceShow of shows) {
    const recommendation = getRowRecommendation(sourceShow);
    if (!recommendation) {
      continue;
    }
    const key = getRecommendationKey(recommendation);
    if (!key || watchlistKeys.has(key)) {
      continue;
    }

    const existing = byRecommendation.get(key);
    const source = {
      id: sourceShow.id,
      slug: sourceShow.slug,
      title: sourceShow.title || "Saved show",
      releaseDate: sourceShow.releaseDate,
    };
    if (existing) {
      existing.sources.push(source);
      continue;
    }

    byRecommendation.set(key, {
      key,
      related: {
        ...recommendation,
        bridge: recommendation.bridge || "",
      },
      sources: [source],
    });
  }

  return Array.from(byRecommendation.values())
    .sort((first, second) => {
      if (second.sources.length !== first.sources.length) {
        return second.sources.length - first.sources.length;
      }
      return String(first.related.title || "").localeCompare(String(second.related.title || ""));
    })
    .slice(0, limit);
};

// A saved show with no page to open (no slug, or taken down) gets a plain poster, not a link.
const PosterFrame = ({ show, href, className, imageClassName }) => {
  const poster = show.poster ? (
    <img src={show.poster} alt="" className={imageClassName} loading="lazy" />
  ) : (
    <PosterPlaceholder showId={show.id} className="h-full w-full bg-poster-placeholder-gradient" />
  );

  return href ? (
    <a href={href} onClick={(event) => handleInternalLinkClick(event, href)} className={className} aria-label={`Open ${show.title}`}>
      {poster}
    </a>
  ) : (
    <div className={className}>{poster}</div>
  );
};

PosterFrame.propTypes = {
  show: PropTypes.object.isRequired,
  href: PropTypes.string,
  className: PropTypes.string,
  imageClassName: PropTypes.string,
};

const RadarShowCard = ({ show, onRemove, recommendationPrefix, gone = false }) => {
  const primaryNetwork = getPrimaryNetwork(show);
  const href = gone ? null : getShowHref(show);
  const rowRecommendation = getRowRecommendation(show);
  const recommendationBridge = getRecommendationBridge(show);

  return (
    <article className="grid grid-cols-[92px_minmax(0,1fr)] gap-4 rounded-xl border border-accent-gold/55 bg-card/75 p-4 shadow-card-base transition-colors hover:border-primary/55 sm:grid-cols-[92px_1fr_auto]">
      <PosterFrame
        show={show}
        href={href}
        className="block h-[138px] w-[92px] overflow-hidden rounded-lg border border-accent-gold/50 bg-muted"
        imageClassName="h-full w-full object-cover grayscale-[18%] contrast-110"
      />

      <div className="min-w-0 space-y-3">
        <div className="space-y-1">
          <Typography as="h3" variant="h4" className="text-foreground">
            {href ? (
              <a href={href} onClick={(event) => handleInternalLinkClick(event, href)} className="title-link">
                {show.title || "Saved show"}
              </a>
            ) : (
              show.title || "Saved show"
            )}
          </Typography>
          <Typography variant="body-small" className="text-muted-foreground">
            {gone ? "No longer listed" : getReleaseRadarLabel(show.releaseDate)}
            {!gone && show.releaseDate ? ` · ${formatSavedReleaseDate(show.releaseDate)}` : ""}
          </Typography>
        </div>

        <div className="flex flex-wrap gap-2">
          {primaryNetwork && (
            <Chip variant="network" size="badge">
              {primaryNetwork}
            </Chip>
          )}
          <SeasonBadge seasonNumber={show.seasonNumber} part={show.seasonPart} />
          {(show.genres || []).slice(0, 2).map((genre) => (
            <Chip key={genre} variant="muted" size="badge">
              {genre}
            </Chip>
          ))}
        </div>

        {rowRecommendation && (
          <div className="space-y-1">
            <a
              href={`/show/${rowRecommendation.slug}`}
              onClick={(event) => handleInternalLinkClick(event, `/show/${rowRecommendation.slug}`)}
              className="inline-flex max-w-full items-center gap-1 text-body-small font-semibold text-muted-foreground transition-colors hover:text-primary"
            >
              <span>{recommendationPrefix}:</span>
              <span className="truncate text-foreground">{rowRecommendation.title}</span>
            </a>
            {recommendationBridge && (
              <Typography variant="body-small" className="reading-measure text-muted-foreground">
                <BridgeText text={recommendationBridge} titles={[show.title, rowRecommendation.title]} />
              </Typography>
            )}
          </div>
        )}
      </div>

      <div className="col-span-2 flex items-start justify-end sm:col-span-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onRemove(show)}
          data-remove-id={show.id}
          aria-label={`Remove ${show.title || "saved show"}`}
          className="text-muted-foreground hover:text-secondary-readable"
        >
          <span className="flex items-center gap-2">
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            Remove
          </span>
        </Button>
      </div>
    </article>
  );
};

RadarShowCard.propTypes = {
  show: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string,
    slug: PropTypes.string,
    poster: PropTypes.string,
    releaseDate: PropTypes.string,
    networks: PropTypes.arrayOf(PropTypes.string),
    sourceName: PropTypes.string,
    seasonNumber: PropTypes.number,
    seasonPart: PropTypes.string,
    genres: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  onRemove: PropTypes.func.isRequired,
  recommendationPrefix: PropTypes.string.isRequired,
  gone: PropTypes.bool,
};

const WatchlistRecommendationRail = ({ recommendations }) => {
  if (recommendations.length < 2) {
    return null;
  }

  const picks = recommendations.slice(0, 3);

  return (
    <ContentSection title="Because you saved these">
      <Typography variant="body-small" className="max-w-[70ch] text-muted-foreground">
        {picks.length} picks that follow on from the shows you saved.
      </Typography>

      <div className="show-recommendations-layout">
        {picks.map((recommendation, index) => (
          <ShowRecommendationCard
            key={recommendation.key}
            related={{ ...recommendation.related, bridge: "" }}
            priority={index === 0}
            surface="my_list_recommendation_rail"
          />
        ))}
      </div>
    </ContentSection>
  );
};

WatchlistRecommendationRail.propTypes = {
  recommendations: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      related: PropTypes.object.isRequired,
      sources: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          slug: PropTypes.string,
          title: PropTypes.string,
          releaseDate: PropTypes.string,
        })
      ).isRequired,
    })
  ).isRequired,
};

const ManageListMenu = ({
  watchlistShows,
  onImportRequest,
  onClearRequest,
  disabled = false,
  triggerRef = null,
}) => {
  const handleExport = (format) => {
    exportWatchlist({ format, shows: watchlistShows });
    trackEvent("watchlist_export", { format, count: watchlistShows.length });
  };

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger asChild ref={triggerRef}>
        <Button type="button" variant="outline" disabled={disabled}>
          <span className="flex items-center gap-2">
            <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
            List options
          </span>
        </Button>
      </DropdownMenu.Trigger>
      {/* The trigger sits left of the text column below md and at the right edge from md up (.my-list-intro). */}
      <DropdownMenu.Content align="start" className="max-h-[75vh] w-[calc(100vw-2rem)] max-w-80 sm:w-80 md:left-auto md:right-0">
        <DropdownMenu.Label>Backup</DropdownMenu.Label>
        <DropdownMenu.Item
          onClick={onImportRequest}
          className="normal-case tracking-normal"
        >
          <Upload className="mr-2 h-4 w-4" aria-hidden="true" />
          Restore from backup file
        </DropdownMenu.Item>
        <DropdownMenu.Separator />
        <DropdownMenu.Label>Export</DropdownMenu.Label>
        {EXPORT_OPTIONS.map((option) => (
          <DropdownMenu.Item
            key={option.value}
            onClick={() => handleExport(option.value)}
            disabled={disabled}
            className="normal-case tracking-normal"
          >
            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            {option.label}
          </DropdownMenu.Item>
        ))}
        <DropdownMenu.Separator />
        <DropdownMenu.Item
          onClick={onClearRequest}
          disabled={disabled}
          className="text-secondary-readable"
        >
          <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
          Clear all saved shows
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};

ManageListMenu.propTypes = {
  watchlistShows: PropTypes.arrayOf(PropTypes.object).isRequired,
  onImportRequest: PropTypes.func.isRequired,
  onClearRequest: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  triggerRef: PropTypes.shape({ current: PropTypes.any }),
};

const RemovedNotice = ({ show = null, message = null, onUndo, onDismiss }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed border-accent-gold/60 bg-card/60 px-4 py-3">
    <Typography variant="body-small" className="min-w-0 text-foreground">
      {message || `Removed ${show.title || "saved show"}.`}
    </Typography>
    <div className="flex shrink-0 gap-2">
      <Button type="button" variant="outline" size="sm" onClick={onUndo}>
        <span className="flex items-center gap-2">
          <Undo2 className="h-4 w-4" aria-hidden="true" />
          Undo
        </span>
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={onDismiss} className="text-muted-foreground">
        Dismiss
      </Button>
    </div>
  </div>
);

RemovedNotice.propTypes = {
  show: PropTypes.object,
  message: PropTypes.string,
  onUndo: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
};

const RadarSection = ({
  title,
  description,
  icon: Icon,
  sectionKey,
  shows,
  onRemove,
  recommendationPrefix,
  goneSlugs,
  removed,
  onUndo,
  onDismiss,
  delay = 0,
}) => {
  // The Undo notice sits where the removed row was, so it comes just before the next row's Remove button.
  const holdsNotice = removed?.section === sectionKey;
  if (!shows.length && !holdsNotice) {
    return null;
  }
  const notice = holdsNotice ? <RemovedNotice show={removed.show} onUndo={onUndo} onDismiss={onDismiss} /> : null;
  const noticeIsLast = holdsNotice && !shows.some((show) => show.id === removed.beforeId);

  return (
  <MotionSection
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.28, ease: [0.0, 0.0, 0.2, 1], delay }}
    className="space-y-4"
    aria-labelledby={`${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-heading`}
  >
    <div className="flex items-center justify-between gap-4 border-b border-dashed border-accent-gold/50 pb-3">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-md border border-primary/50 bg-card text-primary">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
        <div>
          <Typography id={`${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-heading`} as="h2" variant="h3" className="text-foreground">
            {title}
          </Typography>
          <Typography variant="body-small" className="text-muted-foreground">
            {description}
          </Typography>
        </div>
      </div>
      <Chip variant="muted" size="badge" className="text-meta">
        {shows.length}
      </Chip>
    </div>

    <div className="space-y-3">
      {shows.map((show) => (
        <React.Fragment key={show.id}>
          {holdsNotice && removed.beforeId === show.id && notice}
          <RadarShowCard
            show={show}
            onRemove={onRemove}
            recommendationPrefix={recommendationPrefix}
            gone={goneSlugs.has(show.slug)}
          />
        </React.Fragment>
      ))}
      {noticeIsLast && notice}
    </div>
  </MotionSection>
  );
};

RadarSection.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  sectionKey: PropTypes.string.isRequired,
  shows: PropTypes.arrayOf(PropTypes.object).isRequired,
  onRemove: PropTypes.func.isRequired,
  recommendationPrefix: PropTypes.string.isRequired,
  goneSlugs: PropTypes.instanceOf(Set).isRequired,
  removed: PropTypes.shape({
    show: PropTypes.object.isRequired,
    section: PropTypes.string,
    beforeId: PropTypes.string,
  }),
  onUndo: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  delay: PropTypes.number,
};

const MyListPage = () => {
  const {
    entries,
    watchlistCount,
    addToWatchlist,
    removeFromWatchlist,
    clearWatchlist,
    restoreWatchlist,
    upsertWatchlistEntries,
    mergeWatchlistImport,
  } = useWatchlist();
  const [confirmingClear, setConfirmingClear] = useState(false);
  // The last removed show, with where its row sat and where focus went. It stays until dismissed,
  // replaced by another removal, or the visitor leaves the page.
  const [removed, setRemoved] = useState(null);
  // The whole list as it was before Clear all, so Undo can put it back exactly. It replaces `removed`.
  const [cleared, setCleared] = useState(null);
  const [goneSlugs, setGoneSlugs] = useState(() => new Set());
  const [importFeedback, setImportFeedback] = useState(null);
  const checkedSlugsRef = useRef(new Set());
  const pendingFocusRef = useRef(null);
  const emptyHeadingRef = useRef(null);
  const importInputRef = useRef(null);
  const cancelClearRef = useRef(null);
  const listOptionsRef = useRef(null);

  // Focus starts on the safe choice, not the destructive one.
  useEffect(() => {
    if (confirmingClear && cancelClearRef.current) {
      cancelClearRef.current.focus();
    }
  }, [confirmingClear]);
  const { awaiting, ready } = useMemo(() => splitReleaseRadarEntries(entries), [entries]);
  const watchlistRecommendations = useMemo(() => buildWatchlistRecommendations(entries), [entries]);

  usePageMeta({
    title: "My List | PremiereLens",
    description: "Track the TV premieres and recent releases saved to your PremiereLens list.",
    pathname: "/my-list",
    noindex: true,
    ogType: "website",
  });

  const handleClearAll = () => {
    if (!confirmingClear) {
      setConfirmingClear(true);
      return;
    }
    setCleared(entries);
    setRemoved(null);
    clearWatchlist();
    announce(`Cleared ${entries.length} saved ${entries.length === 1 ? "show" : "shows"}. Undo is available.`);
    pendingFocusRef.current = "empty";
    setConfirmingClear(false);
  };

  // Cancelling returns focus to the menu the request came from.
  const handleCancelClear = () => {
    setConfirmingClear(false);
    listOptionsRef.current?.focus();
  };

  const handleUndoClear = () => {
    restoreWatchlist(cleared);
    announce(`Restored ${cleared.length} saved ${cleared.length === 1 ? "show" : "shows"}.`);
    pendingFocusRef.current = cleared[0]?.id;
    setCleared(null);
  };

  const handleDismissCleared = () => {
    pendingFocusRef.current = "empty";
    setCleared(null);
  };

  const handleRemoveShow = (show) => {
    const plan = planRemoval({ awaiting, ready }, show.id);
    removeFromWatchlist(show, { silent: true });
    announce(getRemovalAnnouncement(show));
    setCleared(null);
    setRemoved({ show, ...plan });
    pendingFocusRef.current = plan?.focusId ?? "empty";
  };

  const handleUndoRemove = () => {
    addToWatchlist(removed.show, { silent: true });
    announce(getRestoreAnnouncement(removed.show));
    pendingFocusRef.current = removed.show.id;
    setRemoved(null);
  };

  const handleDismissRemoved = () => {
    pendingFocusRef.current = removed.focusId ?? "empty";
    setRemoved(null);
  };

  // Moves focus once the render that follows a remove, undo or dismiss has committed.
  useEffect(() => {
    const target = pendingFocusRef.current;
    if (target === null) {
      return;
    }
    pendingFocusRef.current = null;
    const button = Array.from(document.querySelectorAll("[data-remove-id]")).find(
      (node) => node.dataset.removeId === target
    );
    (target === "empty" ? emptyHeadingRef.current : button)?.focus();
  });

  // Follow a renamed show to its current slug and mark a taken-down one, once per slug.
  // A result applies whenever it arrives: the list often changes while a check is out
  // (the list loading a second time, a pick being added), and the slug isn't checked again.
  useEffect(() => {
    entries.forEach((entry) => {
      if (!entry.slug || checkedSlugsRef.current.has(entry.slug)) {
        return;
      }
      checkedSlugsRef.current.add(entry.slug);
      checkShowSlug(entry.slug).then(({ status, slug }) => {
        if (status === "moved") {
          upsertWatchlistEntries([{ ...entry, slug }]);
        } else if (status === "gone") {
          setGoneSlugs((prev) => new Set(prev).add(entry.slug));
        }
      });
    });
  }, [entries, upsertWatchlistEntries]);

  useEffect(() => {
    if (!entries.some((entry) => entry.slug && !entry.nextWatch)) {
      return undefined;
    }

    let active = true;
    const run = async () => {
      try {
        const payloads = await Promise.allSettled([
          fetchReleases({ dateRange: "next" }),
          fetchReleases({ dateRange: "previous" }),
        ]);
        if (!active) {
          return;
        }
        const shows = payloads
          .flatMap((result) => (result.status === "fulfilled" ? result.value?.data || [] : []))
          .filter((show) => show?.nextWatch);
        upsertWatchlistEntries(shows);
      } catch {
        // Recommendations are a progressive enhancement for saved rows.
      }
    };
    run();

    return () => {
      active = false;
    };
  }, [entries, upsertWatchlistEntries]);

  const handleImportFile = (event) => {
    const file = event.target.files?.[0];
    if (!importInputRef.current) return;
    importInputRef.current.value = "";
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const shows = parseBackupImport(e.target.result);
        const { added, skipped } = mergeWatchlistImport(shows);
        trackEvent("watchlist_import", { added, skipped, total: shows.length });
        setImportFeedback({ added, skipped });
        setTimeout(() => setImportFeedback(null), 5000);
      } catch (err) {
        setImportFeedback({ error: err.message });
        setTimeout(() => setImportFeedback(null), 5000);
      }
    };
    reader.readAsText(file);
  };

  const hasShows = watchlistCount > 0;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SubpageHeader backHref="/" backLabel="Home" showMyListAction={false} />
      <main id="main-content" tabIndex={-1} className="route-shell route-shell--standard">
        <div className="space-y-8">
          <UtilityPageIntro
            title="My List"
            description="Saved in this browser on this device. See what has premiered and what is still coming."
            className="my-list-intro"
            actions={(
              <>
                <input
                  ref={importInputRef}
                  type="file"
                  accept=".json"
                  className="sr-only"
                  tabIndex={-1}
                  onChange={handleImportFile}
                />
                {hasShows && (
                  <ManageListMenu
                    watchlistShows={entries}
                    onImportRequest={() => importInputRef.current?.click()}
                    onClearRequest={() => setConfirmingClear(true)}
                    disabled={!hasShows}
                    triggerRef={listOptionsRef}
                  />
                )}
                <div role="status" aria-live="polite" aria-atomic="true" className="w-full">
                  <AnimatePresence>
                    {importFeedback && (
                      <motion.p
                        key="import-feedback"
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.2, ease: [0.25, 1, 0.5, 1] }}
                        className={cn("text-caption", importFeedback.error ? "text-secondary-readable" : "text-muted-foreground")}
                      >
                        {importFeedback.error
                          ? importFeedback.error
                          : `${importFeedback.added} added, ${importFeedback.skipped} already saved`}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
              </>
            )}
          />

          {confirmingClear && (
            <section
              className="rounded-xl border border-secondary/50 bg-card/80 p-4 shadow-card-base"
              aria-labelledby="clear-list-confirmation-title"
              onKeyDown={(event) => {
                if (event.key === "Escape") handleCancelClear();
              }}
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 space-y-1">
                  <Typography id="clear-list-confirmation-title" as="h2" variant="h4" className="text-foreground">
                    Clear all saved shows?
                  </Typography>
                  <Typography variant="body-small" className="text-muted-foreground">
                    This removes {watchlistCount} saved {watchlistCount === 1 ? "show" : "shows"} from this device. Export a backup first if you want to restore the list later.
                  </Typography>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  <Button ref={cancelClearRef} type="button" variant="ghost" onClick={handleCancelClear}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleClearAll}
                  >
                    <span className="flex items-center gap-2">
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                      Clear all
                    </span>
                  </Button>
                </div>
              </div>
            </section>
          )}

          {!hasShows ? (
            <section className="rounded-xl border border-accent-gold/50 bg-card/65 p-6 text-center shadow-card-base">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-md border border-primary/50 bg-background text-primary">
                <Heart className="h-5 w-5" aria-hidden="true" />
              </div>
              <Typography ref={emptyHeadingRef} tabIndex={-1} as="h2" variant="h3" className="mx-auto w-fit text-foreground">
                Save shows to build My List
              </Typography>
              {removed && (
                <div className="mx-auto mt-3 max-w-xl text-left">
                  <RemovedNotice show={removed.show} onUndo={handleUndoRemove} onDismiss={handleDismissRemoved} />
                </div>
              )}
              {cleared && (
                <div className="mx-auto mt-3 max-w-xl text-left">
                  <RemovedNotice
                    message={`Cleared ${cleared.length} saved ${cleared.length === 1 ? "show" : "shows"}.`}
                    onUndo={handleUndoClear}
                    onDismiss={handleDismissCleared}
                  />
                </div>
              )}
              <Typography variant="body" className="mx-auto mt-2 max-w-xl text-muted-foreground">
                Use the heart button on any show to track upcoming premieres and recently released picks here.
              </Typography>
              <div className="mt-5 flex flex-wrap justify-center gap-3">
                <Button variant="default" asChild>
                  <a href="/" onClick={(event) => handleInternalLinkClick(event, "/")}>
                    Browse upcoming shows
                  </a>
                </Button>
                <Button variant="outline" asChild>
                  <a href="/most-recommended" onClick={(event) => handleInternalLinkClick(event, "/most-recommended")}>
                    Get recommendations
                  </a>
                </Button>
              </div>
              <details className="mx-auto mt-5 max-w-sm text-left">
                <summary className="flex min-h-11 cursor-pointer list-none items-center justify-center text-body-small font-semibold text-muted-foreground hover:text-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring">
                  Have a saved backup?
                </summary>
                <div className="flex justify-center pt-2">
                  <Button type="button" variant="outline" onClick={() => importInputRef.current?.click()}>
                    <Upload className="h-4 w-4" aria-hidden="true" />
                    Restore backup
                  </Button>
                </div>
              </details>
            </section>
          ) : (
            <div className="space-y-10">
              {watchlistCount >= BACKUP_REMINDER_COUNT && (
                <Typography variant="body-small" className="text-muted-foreground">
                  My List lives only in this browser. Use List options to export a backup.
                </Typography>
              )}
              <RadarSection
                title="Awaiting premiere"
                description="Saved shows with release dates still ahead."
                icon={CalendarClock}
                sectionKey="awaiting"
                shows={awaiting}
                onRemove={handleRemoveShow}
                recommendationPrefix="While you wait, watch"
                goneSlugs={goneSlugs}
                removed={removed}
                onUndo={handleUndoRemove}
                onDismiss={handleDismissRemoved}
              />
              <RadarSection
                title="Released and ready"
                description="Saved shows that have already premiered, plus anything without a confirmed date."
                icon={CheckCircle2}
                sectionKey="ready"
                shows={ready}
                onRemove={handleRemoveShow}
                recommendationPrefix="After this, watch"
                goneSlugs={goneSlugs}
                removed={removed}
                onUndo={handleUndoRemove}
                onDismiss={handleDismissRemoved}
                delay={0.05}
              />
              <WatchlistRecommendationRail recommendations={watchlistRecommendations} />
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

export default MyListPage;
